import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Monitor, FileText, Users, Target, Lightbulb } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl mb-6">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Recursos Didácticos para
              <span className="text-emerald-600 block">Lectoescritura</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Descubre una colección especializada de recursos educativos desarrollados por el 
              Departamento de Lenguas de la Universidad Pedagógica Nacional para fortalecer 
              las competencias de lectura y escritura.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 hover:border-emerald-200">
              <CardHeader className="text-center pb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <Monitor className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-xl font-semibold text-gray-900">
                  Recursos Digitales
                </CardTitle>
                <CardDescription className="text-gray-600">
                  Herramientas tecnológicas, aplicaciones, videojuegos y contenido multimedia
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button 
                  asChild 
                  className="w-full bg-blue-600 hover:bg-blue-700 transition-all duration-200 group-hover:bg-blue-700"
                >
                  <Link to="/recursos-digitales" className="inline-flex items-center justify-center space-x-2">
                    <span>Explorar Recursos</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 hover:border-emerald-200">
              <CardHeader className="text-center pb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-xl font-semibold text-gray-900">
                  Recursos Impresos
                </CardTitle>
                <CardDescription className="text-gray-600">
                  Libros, guías, cuentos, crónicas y material didáctico especializado
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button 
                  asChild 
                  className="w-full bg-emerald-600 hover:bg-emerald-700 transition-all duration-200 group-hover:bg-emerald-700"
                >
                  <Link to="/recursos-impresos" className="inline-flex items-center justify-center space-x-2">
                    <span>Ver Catálogo</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 hover:border-emerald-200">
              <CardHeader className="text-center pb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-xl font-semibold text-gray-900">
                  Sitios de Interés
                </CardTitle>
                <CardDescription className="text-gray-600">
                  Plataformas y herramientas web recomendadas para la enseñanza
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button 
                  asChild 
                  className="w-full bg-purple-600 hover:bg-purple-700 transition-all duration-200 group-hover:bg-purple-700"
                >
                  <Link to="/sitios-interes" className="inline-flex items-center justify-center space-x-2">
                    <span>Ver Sitios</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              ¿Por qué usar nuestros recursos?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Todos nuestros materiales están basados en investigación académica rigurosa 
              y han sido validados en contextos educativos reales.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-2xl mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                <Users className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Basado en Investigación</h3>
              <p className="text-gray-600 leading-relaxed">
                Cada recurso proviene de trabajos de grado y proyectos de investigación 
                del Departamento de Lenguas de la UPN.
              </p>
            </div>

            <div className="text-center group">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                <Target className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Resultados Comprobados</h3>
              <p className="text-gray-600 leading-relaxed">
                Todos los proyectos incluyen datos sobre su efectividad y 
                resultados obtenidos en implementaciones reales.
              </p>
            </div>

            <div className="text-center group">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-100 to-amber-200 rounded-2xl mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                <Lightbulb className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Innovación Pedagógica</h3>
              <p className="text-gray-600 leading-relaxed">
                Metodologías innovadoras que combinan teoría pedagógica con 
                herramientas tecnológicas actuales.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-emerald-50 to-teal-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Comienza a Explorar
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Navega por nuestro catálogo de recursos y encuentra las herramientas 
            perfectas para enriquecer tus prácticas de enseñanza de lectoescritura.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700 transition-colors duration-200"
            >
              <Link to="/recursos-digitales" className="inline-flex items-center space-x-2">
                <Monitor className="w-5 h-5" />
                <span>Recursos Digitales</span>
              </Link>
            </Button>
            <Button 
              asChild 
              size="lg" 
              variant="outline" 
              className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 transition-colors duration-200"
            >
              <Link to="/recursos-impresos" className="inline-flex items-center space-x-2">
                <FileText className="w-5 h-5" />
                <span>Recursos Impresos</span>
              </Link>
            </Button>
            <Button 
              asChild 
              size="lg" 
              variant="outline" 
              className="border-purple-600 text-purple-600 hover:bg-purple-50 transition-colors duration-200"
            >
              <Link to="/sitios-interes" className="inline-flex items-center space-x-2">
                <Target className="w-5 h-5" />
                <span>Sitios de Interés</span>
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;